import './ShapeUploader.css';
import React, { useState, useRef, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { SHPLoader } from '@loaders.gl/shapefile';
import { load } from '@loaders.gl/core';
import { Vector as VectorSource } from 'ol/source';
import { Vector } from 'ol/layer';
import { GeoJSON } from 'ol/format';
import { zoom, center } from '../../../../_config/map';
import { Circle as CircleStyle, Fill, Stroke, Style } from 'ol/style';
import Circle from 'ol/geom/Circle.js';

const image = new CircleStyle({
  radius: 5,
  fill: null,
  stroke: new Stroke({ color: 'red', width: 1 }),
});

const styles = {
  Point: new Style({ image }),
  LineString: new Style({ stroke: new Stroke({ color: 'green', width: 1 }) }),
  MultiLineString: new Style({ stroke: new Stroke({ color: 'green', width: 1 }) }),
  MultiPoint: new Style({ image }),
  MultiPolygon: new Style({
    stroke: new Stroke({ color: 'yellow', width: 1 }),
    fill: new Fill({ color: 'rgba(255, 255, 0, 0.1)' }),
  }),
  Polygon: new Style({
    stroke: new Stroke({ color: 'blue', lineDash: [4], width: 3 }),
    fill: new Fill({ color: 'rgba(0, 0, 255, 0.1)' }),
  }),
  GeometryCollection: new Style({
    stroke: new Stroke({ color: 'magenta', width: 2 }),
    fill: new Fill({ color: 'magenta' }),
    image: new CircleStyle({ radius: 10, fill: null, stroke: new Stroke({ color: 'magenta' }) }),
  }),
  Circle: new Style({
    stroke: new Stroke({ color: 'red', width: 2 }),
    fill: new Fill({ color: 'rgba(255,0,0,0.2)' }),
  }),
};

const styleFunction = (feature) => styles[feature.getGeometry().getType()];

let vectorForShape = null;

function ShapeUploader(props) {
  const { t } = useTranslation();
  const htmlElement = useRef();
  const feedbackElement = useRef();
  const [file, setFile] = useState(undefined);
  const zIndexer = useState(props.map.getLayers().getArray().length)[0];
  const reader = new FileReader();

  const handleChange = (e) => {
    const files = e.target.files;
    if (files && files[0]) setFile(files[0]);
  };

  const convertShapeToFeatures = async () => {
    try {
      if (!file) return showErrorFeedback();
      handleReset();

      reader.onload = async () => {
        try {
          const result = await load(reader.result, SHPLoader);
          const geojsonObjects = convertGeometriesToGeojson(result.geometries);

          const vectorSource = new VectorSource({
            features: new GeoJSON().readFeatures(geojsonObjects, {
              dataProjection: 'EPSG:4326',
              featureProjection: props.map.getView().getProjection(),
            }),
          });

          const vector = new Vector({
            name: 'SHP',
            title: 'SHP',
            source: vectorSource,
            style: styleFunction,
          });

          vector.setZIndex(zIndexer + 5);
          props.map.addLayer(vector);
          vectorForShape = vector;

          props.map.getView().fit(vectorSource.getExtent(), props.map.getSize());
        } catch (error) {
          console.error(error);
          showErrorFeedback();
        }
      };

      reader.readAsDataURL(file);
      props.callback();
    } catch (error) {
      console.error(error);
      showErrorFeedback();
    }
  };

  const convertGeometriesToGeojson = (data) => ({
    type: 'FeatureCollection',
    crs: { type: 'name', properties: { name: 'EPSG:4326' } },
    features: data.map((geometry) => convertGeometryToFeature(geometry)),
  });

  const convertGeometryToFeature = (geometry) => ({
    type: 'Feature',
    geometry: {
      type: geometry.type,
      coordinates: extractCoordinates(geometry),
    },
  });



  const extractCoordinates = (geometry) => {
    const { type, positions } = geometry;
    const coords = positions.value; 
    const size = positions.size || 2; 

    if (type === 'Point') {
      return toPair(coords, 0, size);
    }
    if (type === 'LineString') {
      return extractLineString(coords, size);
    }
    if (type === 'MultiLineString') {
      return extractMultiLineString(geometry, coords, size);
    }
    if (type === 'Polygon') {
      return extractPolygon(geometry, coords, size);
    }
    if (type === 'MultiPolygon') {
      return extractMultiPolygon(geometry, coords, size);
    }
    throw new Error('Geometria inválida!');
  };

  const toPair = (coords, i, size) => [coords[i * size], coords[i * size + 1]];

  const sliceToPairs = (coords, startIdx, endIdx, size) => {
    const result = [];
    for (let i = startIdx; i < endIdx; i++) {
      result.push(toPair(coords, i, size));
    }
    return result;
  };

  const extractLineString = (coords, size) => {
    const numPoints = coords.length / size;
    return sliceToPairs(coords, 0, numPoints, size);
  };

  const extractMultiLineString = (geometry, coords, size) => {
    const pathIndices = geometry.pathIndices?.value;
    if (!pathIndices) {
      return [extractLineString(coords, size)];
    }
    const lines = [];
    for (let p = 0; p < pathIndices.length - 1; p++) {
      lines.push(sliceToPairs(coords, pathIndices[p], pathIndices[p + 1], size));
    }
    return lines;
  };

  const extractPolygon = (geometry, coords, size) => {
    const ringIndices =
      geometry.polygonIndices?.value || geometry.primitivePolygonIndices?.value;

    if (!ringIndices) {
      return [extractLineString(coords, size)];
    }

    const rings = [];
    for (let r = 0; r < ringIndices.length - 1; r++) {
      rings.push(sliceToPairs(coords, ringIndices[r], ringIndices[r + 1], size));
    }
    return rings; 
  };

  const extractMultiPolygon = (geometry, coords, size) => {
    const ringIndices = geometry.primitivePolygonIndices?.value;
    const polyIndices = geometry.polygonIndices?.value;

    if (!ringIndices || !polyIndices) {
      return [extractPolygon(geometry, coords, size)];
    }

    const polygons = [];
    for (let p = 0; p < polyIndices.length - 1; p++) {
      const ringStart = polyIndices[p];
      const ringEnd = polyIndices[p + 1];
      const rings = [];
      for (let r = ringStart; r < ringEnd; r++) {
        rings.push(sliceToPairs(coords, ringIndices[r], ringIndices[r + 1], size));
      }
      polygons.push(rings);
    }
    return polygons;
  };

 

  const handleReset = () => {
    if (vectorForShape) {
      props.map.removeLayer(vectorForShape);
      vectorForShape.getSource().clear();
      vectorForShape = undefined;
    }
  };

  const showErrorFeedback = () => {
    feedbackElement.current.style.visibility = 'visible';
  };

  useEffect(() => {
    if (file) convertShapeToFeatures();
  }, [file]);

  useEffect(() => {
    feedbackElement.current.style.visibility = 'hidden';
  }, []);

  return (
    <div className="ShapeUploader">
      <h6>
        <label htmlFor="ShapeFormFile" className="form-label">
          {t('shapeUploader.label')}
        </label>
      </h6>

      <div className="input-group mb-3">
        <input
          ref={htmlElement}
          className="form-control"
          id="ShapeFormFile"
          type="file"
          accept=".zip,.shp"
          onChange={handleChange}
          style={{ borderRadius: '10px 0px 0px 10px' }}
        />
        <span onClick={handleReset} className="input-group-text ShapeUploaderReset">
          {t('shapeUploader.reset')}
        </span>
      </div>

      <div className="ShapeUploaderHint">
        {t('shapeUploader.hint')}
      </div>

      <div ref={feedbackElement} className="ShapeUploaderErrorFeedback">
        {t('shapeUploader.error')}
      </div>
    </div>
  );
}

export default ShapeUploader;